"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import React, { useRef } from "react";

import { CaseStudyData } from "../../data/case-studies/types";

interface CaseStudyHeroProps {
  caseStudy: CaseStudyData;
}

export function CaseStudyHero({ caseStudy }: CaseStudyHeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageFrameRef = useRef<HTMLDivElement>(null);

  // GSAP Subtle Parallax on the Product Viewport
  useGSAP(
    () => {
      if (!imageFrameRef.current) return;

      gsap.fromTo(
        imageFrameRef.current,
        { y: 0 },
        {
          y: -25,
          ease: "none",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        }
      );
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="pt-28 md:pt-36 pb-16 md:pb-24 border-b border-white/[0.08] relative overflow-hidden">
      
      {/* Ambient Theme Color Glow (Reflects authentic product palette) */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full blur-[140px] opacity-[0.12] pointer-events-none -z-10"
        style={{ backgroundColor: caseStudy.accentHex }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Top Return Action */}
        <div className="mb-10">
          <Link
            href="/work"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Portfolio</span>
          </Link>
        </div>

        {/* Monumental Headline */}
        <div className="space-y-4 mb-10 md:mb-14">
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
            className="text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] font-bold tracking-tight text-white leading-none"
          >
            {caseStudy.name}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-slate-300 font-light tracking-tight max-w-5xl leading-tight"
          >
            {caseStudy.title}
          </motion.p>
        </div>

        {/* 3-Column Swiss Metadata Grid with 1px Hairlines */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 py-8 border-y border-white/[0.12] mb-12 md:mb-16">
          <div>
            <span className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Client
            </span>
            <span className="text-sm md:text-base font-medium text-white">
              {caseStudy.client}
            </span>
          </div>

          <div>
            <span className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Stack & Discipline
            </span>
            <span className="text-sm md:text-base font-medium text-white">
              {caseStudy.discipline.slice(0, 2).join(" • ")}
            </span>
          </div>

          <div>
            <span className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              Year
            </span>
            <span className="text-sm md:text-base font-medium text-white">
              {caseStudy.year}
            </span>
          </div>
        </div>

        {/* Signature Full-Width Hero Product Viewport with GSAP Parallax */}
        <div
          ref={imageFrameRef}
          className="rounded-3xl overflow-hidden shadow-2xl bg-zinc-950 border border-white/15 relative"
        >
          {/* Subtle Browser Window Bar */}
          <div className="w-full h-10 bg-zinc-900/90 border-b border-white/10 px-4 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
            </div>
            <div className="mx-auto font-mono text-[11px] text-zinc-400 truncate max-w-xs">
              https://production.{caseStudy.slug}.cloud/workspace
            </div>
          </div>

          <div className="relative w-full aspect-[16/10]">
            <Image
              src={caseStudy.heroImage}
              alt={caseStudy.name}
              fill
              priority
              className="object-cover"
              sizes="100vw"
            />
          </div>
        </div>

      </div>
    </section>
  );
}
